import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import {
  RefreshCw, MapPin, ChevronDown, Clock, ArrowLeft,
} from "lucide-react";
import FastagDashboardMap from "./FastagDashboardMap";
import VehicleSidebar from "./VehicleSidebar";
import TransactionSidebar from "./TransactionSidebar";
import TransactionDetailsPanel from "./TransactionDetailsPanel";
import {
  useTrackedVehicles,
  useAllFastagFromDB,
  useRefreshStateVehicles,
  useRefreshAllVehicles,
} from "../../hooks/useFastagQueries";

const fmtClock = (date) =>
  date?.toLocaleTimeString("en-IN", {
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }) ?? null;

const INDIAN_STATES = [
  "ANDHRA PRADESH", "ARUNACHAL PRADESH", "ASSAM", "BIHAR", "CHHATTISGARH",
  "DELHI", "GOA", "GUJARAT", "HARYANA", "HIMACHAL PRADESH", "JHARKHAND",
  "KARNATAKA", "KERALA", "MADHYA PRADESH", "MAHARASHTRA", "MANIPUR",
  "MEGHALAYA", "MIZORAM", "NAGALAND", "ODISHA", "PUNJAB", "RAJASTHAN",
  "SIKKIM", "TAMIL NADU", "TELANGANA", "TRIPURA", "UTTAR PRADESH",
  "UTTARAKHAND", "WEST BENGAL", "CHANDIGARH", "JAMMU AND KASHMIR",
  "LADAKH", "PUDUCHERRY",
];

const STATE_CENTERS = {
  "ODISHA":           [84.8, 20.9],
  "KARNATAKA":        [75.7, 15.3],
  "MAHARASHTRA":      [75.7, 19.7],
  "UTTAR PRADESH":    [80.9, 26.8],
  "RAJASTHAN":        [74.2, 27.0],
  "MADHYA PRADESH":   [78.6, 23.4],
  "GUJARAT":          [71.5, 22.2],
  "HARYANA":          [76.0, 29.0],
  "PUNJAB":           [75.3, 31.1],
  "BIHAR":            [85.3, 25.9],
  "WEST BENGAL":      [87.8, 23.5],
  "TAMIL NADU":       [78.6, 11.1],
  "TELANGANA":        [79.0, 17.8],
  "ANDHRA PRADESH":   [79.7, 15.9],
  "CHHATTISGARH":     [81.8, 21.3],
  "JHARKHAND":        [85.2, 23.6],
  "ASSAM":            [92.9, 26.2],
  "KERALA":           [76.2, 10.8],
  "DELHI":            [77.1, 28.6],
  "HIMACHAL PRADESH": [77.1, 31.9],
  "UTTARAKHAND":      [79.0, 30.0],
  "GOA":              [74.1, 15.2],
  "CHANDIGARH":       [76.7, 30.7],
  "JAMMU AND KASHMIR":[74.8, 33.7],
  "LADAKH":           [77.5, 34.1],
  "PUDUCHERRY":       [79.8, 11.9],
  "MANIPUR":          [93.9, 24.6],
  "MEGHALAYA":        [91.3, 25.4],
  "MIZORAM":          [92.7, 23.1],
  "NAGALAND":         [94.5, 26.1],
  "SIKKIM":           [88.5, 27.5],
  "TRIPURA":          [91.9, 23.7],
  "ARUNACHAL PRADESH":[94.7, 28.2],
};

const FastagStateDashboard = () => {
  const [selectedState, setSelectedState]             = useState("all");
  const [dropdownOpen, setDropdownOpen]               = useState(false);
  const [selectedVehicleNo, setSelectedVehicleNo]     = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactions, setShowTransactions]       = useState(false);
  const [stateRefreshing, setStateRefreshing] = useState(false);
  const mapRef = useRef(null);

  const { data: vehicles = [] } = useTrackedVehicles();

  const {
    data: vehicleDataMap = {},
    isLoading,
    isFetching,
    dataUpdatedAt,
  } = useAllFastagFromDB(selectedState);

  const { data: allVehicleDataMap = {} } = useAllFastagFromDB("all");

  const refreshMutation = useRefreshAllVehicles();
  const refreshStateMutation = useRefreshStateVehicles();
  const isRefreshing    = refreshMutation.isPending || isFetching;
  const lastRefreshed   = dataUpdatedAt ? new Date(dataUpdatedAt) : null;

  const filteredVehicles = useMemo(() => {
    if (selectedState === "all") return vehicles;
    return vehicles.filter((v) => {
      const txns = vehicleDataMap[v.vehicleRegNo] || [];
      return txns.some((t) => t.tollState === selectedState);
    });
  }, [vehicles, vehicleDataMap, selectedState]);

  const transactions = useMemo(
    () => (selectedVehicleNo ? vehicleDataMap[selectedVehicleNo] || [] : []),
    [selectedVehicleNo, vehicleDataMap]
  );

  const totalTxns = useMemo(
    () => Object.values(vehicleDataMap).reduce((sum, t) => sum + t.length, 0),
    [vehicleDataMap]
  );

  useEffect(() => {
  if (selectedVehicleNo) return;
  if (selectedState === "all") {
    setTimeout(() => mapRef.current?.flyToCenter([77.23, 28.61], 3.8), 300);
    return
  };
  if (!Object.keys(vehicleDataMap).length) {
    const center = STATE_CENTERS[selectedState];
    if (center) setTimeout(() => mapRef.current?.flyToCenter(center), 300);
    return
  };
  setTimeout(() => mapRef.current?.focusOnAllVehicles(vehicleDataMap), 300);
}, [vehicleDataMap, selectedState]);

  const handleVehicleSelect = useCallback((vehicleRegNo) => {
    setSelectedVehicleNo(vehicleRegNo);
    setSelectedTransaction(null);
    setShowTransactions(false);
    const txns = vehicleDataMap[vehicleRegNo] || [];
    setTimeout(() => mapRef.current?.focusOnRoute(txns), 300);
  }, [vehicleDataMap]);

  const handleBackToVehicles = useCallback(() => {
    setShowTransactions(false);
    setSelectedTransaction(null);
    // Deselect vehicle — map goes back to fleet markers
    setSelectedVehicleNo(null);
    setTimeout(() => mapRef.current?.focusOnAllVehicles(vehicleDataMap), 300);
  }, []);

  const handleTransactionSelect = useCallback((txn) => {
    setSelectedTransaction(txn);
    setShowTransactions(true);
    mapRef.current?.focusOnTransaction(txn);
  }, []);

  const handleStateChange = (state) => {
    setSelectedState(state);
    setDropdownOpen(false);
    setSelectedVehicleNo(null);
    setSelectedTransaction(null);
    setShowTransactions(false);

    if (state !== "all") {
    setStateRefreshing(true);
    refreshStateMutation.mutate(state, {
      onSuccess: () => setStateRefreshing(false),
      onError:   () => setStateRefreshing(false),
    });
  }
  };

  const allStates = ["all", ...INDIAN_STATES];

  return (
    <div
      className="flex flex-col h-screen bg-slate-100 overflow-hidden font-sans"
      onClick={() => setDropdownOpen(false)}
    >
      {/* ══ HEADER ══ */}
      <header className="bg-slate-900 text-white px-5 py-3 flex items-center justify-between gap-4 flex-shrink-0 shadow-lg">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-amber-400 flex items-center justify-center text-slate-900 font-black text-base shrink-0">
            ⬡
          </div>
          <div>
            <h1 className="text-sm font-semibold leading-tight">FASTag State Dashboard</h1>
            <p className="text-[10px] text-slate-400 mt-0.5 leading-none">
              {selectedVehicleNo ? selectedVehicleNo : "State-wise toll intelligence"}
            </p>
          </div>
          {selectedVehicleNo && (
            <span className="font-mono text-xs font-bold bg-slate-700 px-3 py-1 rounded-full tracking-widest border border-slate-600">
              {selectedVehicleNo}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <StatPill label="Vehicles"     value={filteredVehicles.length} />
          <StatPill label="Transactions" value={totalTxns} />
          {selectedVehicleNo && transactions.length > 0 && (
            <>
              <StatPill
                label="Plazas"
                value={new Set(transactions.map((t) => t.tollPlazaName)).size}
                color="bg-emerald-600"
              />
              <StatPill label="Vehicle Type" value={transactions[0]?.vehicleType || "—"} />
            </>
          )}
          {isFetching && (
            <span className="flex items-center gap-1 text-[10px] text-amber-300 bg-amber-500/20 px-2 py-1 rounded-lg border border-amber-500/30">
              <RefreshCw size={10} className="animate-spin" /> Updating…
            </span>
          )}
          {lastRefreshed && !isFetching && (
            <span className="text-[10px] text-slate-400 flex items-center gap-1">
              <Clock size={10} /> {fmtClock(lastRefreshed)}
            </span>
          )}
        </div>

        <button
          onClick={() => refreshMutation.mutate()}
          disabled={isRefreshing}
          className="flex items-center gap-1.5 text-xs font-medium bg-slate-700 hover:bg-slate-600 disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 rounded-lg border border-slate-600 transition-colors"
        >
          <RefreshCw size={13} className={isRefreshing ? "animate-spin" : ""} />
          Refresh All
        </button>
      </header>

      {/* ══ FILTER BAR ══ */}
      <div className="bg-white border-b border-slate-200 px-5 py-2.5 flex items-center gap-4 flex-shrink-0">
        <div className="flex items-center gap-2 shrink-0">
          <MapPin size={14} className="text-slate-400" />
          <span className="text-xs font-semibold text-slate-500">Filter by State</span>
        </div>

        <div className="relative" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setDropdownOpen((o) => !o)}
            className="flex items-center gap-2 bg-white border border-slate-200 hover:border-amber-400 rounded-lg px-3 py-2 text-xs font-semibold text-slate-700 min-w-[200px] transition-colors"
          >
            <span className="flex-1 text-left">
              {selectedState === "all" ? "All States" : selectedState}
            </span>
            <ChevronDown
              size={13}
              className={`text-slate-400 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
            />
          </button>

          {dropdownOpen && (
            <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden min-w-[220px] max-h-72 overflow-y-auto">
              {allStates.map((state) => {
                const count = state === "all"
                  ? vehicles.length
                  : vehicles.filter((v) => {
                      const txns = allVehicleDataMap[v.vehicleRegNo] || [];
                      return txns.some((t) => t.tollState === state);
                    }).length;

                return (
                  <button
                    key={state}
                    onClick={() => handleStateChange(state)}
                    className={`w-full flex items-center justify-between px-4 py-2.5 text-xs transition-colors
                      ${selectedState === state
                        ? "bg-amber-50 text-amber-700 font-bold"
                        : "text-slate-700 hover:bg-slate-50"
                      }`}
                  >
                    <span>{state === "all" ? "All States" : state}</span>
                    {count > 0 && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full
                        ${selectedState === state
                          ? "bg-amber-200 text-amber-800"
                          : "bg-slate-100 text-slate-500"
                        }`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {selectedState !== "all" && (
          <button
            onClick={() => handleStateChange("all")}
            className="text-xs text-amber-600 hover:text-amber-800 font-medium flex items-center gap-1"
          >
            ✕ Clear
          </button>
        )}

        {/* Sidebar toggle — only when a vehicle is selected */}
        {selectedVehicleNo && (
          <div className="flex items-center gap-2 ml-2">
            <button
              onClick={handleBackToVehicles}
              className="flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-800 border border-slate-200 hover:border-slate-300 px-3 py-1.5 rounded-lg transition-colors"
            >
              <ArrowLeft size={12} /> All vehicles
            </button>
            <button
              onClick={() => setShowTransactions((v) => !v)}
              className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                showTransactions
                  ? "bg-amber-400 text-slate-900 border-amber-400"
                  : "bg-white text-slate-600 border-slate-200 hover:border-amber-300"
              }`}
            >
              Transactions
              {transactions.length > 0 && (
                <span className="ml-1.5 bg-slate-200 text-slate-600 text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  {transactions.length}
                </span>
              )}
            </button>
          </div>
        )}

        <div className="ml-auto flex items-center gap-2 text-[10px] text-slate-400">
          <span className={`w-2 h-2 rounded-full ${isRefreshing ? "bg-amber-400 animate-pulse" : "bg-emerald-400"}`} />
          {isRefreshing ? "Refreshing…" : "Live"}
          {lastRefreshed && <span>· {fmtClock(lastRefreshed)}</span>}
        </div>
      </div>

      {/* ══ BODY ══ */}
      <div className="flex flex-1 overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center flex-1 gap-3 text-slate-400">
            <div className="w-9 h-9 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm">Loading toll data…</p>
          </div>
        ) : (
          <>
            {/* ── Sidebar ── */}
            {!showTransactions ? (
              <VehicleSidebar
                vehicles={filteredVehicles}
                vehicleDataMap={vehicleDataMap}
                selectedVehicleNo={selectedVehicleNo}
                onVehicleSelect={handleVehicleSelect}
                isFetching={isFetching}
              />
            ) : (
              <div className="flex flex-col w-64 flex-shrink-0">
                <button
                  onClick={() => { setShowTransactions(false); setSelectedTransaction(null); }}
                  className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium text-slate-500 hover:text-slate-800 bg-white border-b border-r border-slate-200 transition-colors"
                >
                  <ArrowLeft size={12} /> Back to vehicles
                </button>
                <TransactionSidebar
                  transactions={transactions}
                  selectedTransaction={selectedTransaction}
                  onTransactionSelect={handleTransactionSelect}
                />
              </div>
            )}

            {/* ── Map ── */}
            <div className="flex-1 relative overflow-hidden">
              {isFetching && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 bg-slate-900/80 text-white text-[10px] font-medium px-3 py-1.5 rounded-full shadow-lg backdrop-blur-sm pointer-events-none">
                  <RefreshCw size={10} className="animate-spin text-amber-400" />
                  Refreshing toll data…
                </div>
              )}
               {stateRefreshing && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 bg-amber-500 text-slate-900 text-[10px] font-semibold px-4 py-2 rounded-full shadow-lg pointer-events-none">
                  <RefreshCw size={11} className="animate-spin" />
                  Fetching latest toll data for {selectedState}…
                </div>
              )}
              <FastagDashboardMap
                ref={mapRef}
                vehicleDataMap={vehicleDataMap}
                filteredVehicles={filteredVehicles}
                selectedVehicleNo={selectedVehicleNo}
                selectedTransaction={selectedTransaction}
                onVehicleSelect={handleVehicleSelect}
                onTransactionSelect={handleTransactionSelect}
              />
            </div>

            {/* ── Transaction detail panel ── */}
            {selectedTransaction && (
              <TransactionDetailsPanel
                transaction={selectedTransaction}
                onClose={() => setSelectedTransaction(null)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const StatPill = ({ label, value, color = "bg-slate-700" }) => (
  <div className={`${color} rounded-lg px-3 py-1.5 text-center min-w-[64px] border border-white/10`}>
    <p className="text-[10px] text-white/60 leading-none">{label}</p>
    <p className="text-sm font-semibold text-white leading-none mt-1">{value}</p>
  </div>
);

export default FastagStateDashboard;